'use strict';

class StickyHandler {
  constructor({
    $element,
    $wrapper = $element,
    bottom = false,
    staySticky = false,
    onStateChange = function () {},
    urgencyMessaging = { shouldNotify: false, toastId: '' },
  }) {
    this.$element = $element;
    this.$wrapper = $wrapper;
    if ($wrapper) {
      this.$prevElement = this.$wrapper.previousElementSibling;
    }
    if (!this.$element) {
      return;
    }

    this.bottom = bottom;
    this.staySticky = staySticky;
    this.urgencyMessaging = urgencyMessaging;
    this.onStateChange = onStateChange;

    this.stickyClasses = ['sticky', `sticky-${this.bottom ? 'bottom' : 'top'}`];
    this.fixed = this.$element.classList.contains('sticky');

    this.observer = new window.IntersectionObserver(
      this.onIntersect.bind(this),
      {
        rootMargin: this.bottom
          ? '0px'
          : `${
              window.getComputedStyle(this.$element).paddingTop || '0px'
            } 0px 0px 0px`,
        threshold: this.bottom ? 1 : [0, 1],
      }
    );
    this.observedElement = this.bottom ? this.$element : this.$prevElement;
    this.observer.observe(this.observedElement);

    if (this.bottom) {
      this._checkForAutoScroll();
    }

    // listener to hide sticky handler after coming back from modal
    window.addEventListener(
      'hashchange',
      (() => {
        if (this.fixed && !this.staySticky) {
          this.change(false);
        }
      }).bind(this),
      false
    );
  }

  _checkForAutoScroll() {
    /*
      Intersection Observer does not work when user reloads the page after scrolling
      Need to do one time check to see if browser is auto-scrolling
    */
    const checkIfAutoScrolled = (() => {
      const scrollElem =
        this.$element.getBoundingClientRect().top + document.body.scrollTop;

      if (scrollElem < window.pageYOffset) {
        this.change(true);
      }
      window.removeEventListener('scroll', checkIfAutoScrolled);
    }).bind(this);
    window.addEventListener('scroll', checkIfAutoScrolled, false);
  }

  onIntersect(entries) {
    entries.forEach(entry => {
      const {
        intersectionRatio,
        rootBounds,
        boundingClientRect,
        isIntersecting,
      } = entry;
      if (
        !this.bottom &&
        rootBounds &&
        rootBounds.bottom < boundingClientRect.top
      ) {
        this.change(false);
      } else if (!isIntersecting && intersectionRatio === 0) {
        this.change(!this.bottom, this.urgencyMessaging.shouldNotify); // this is the case for showing the top bar
      } else if (intersectionRatio === 1 && this.bottom) {
        this.change(true);
      } else if (isIntersecting && !this.bottom && this.fixed) {
        this.change(false);
      }
    });
  }

  change(shouldBeFixed, urgencyMessaging) {
    const changeToFixed = !this.fixed && shouldBeFixed;
    const changeToUnfixed = this.fixed && !shouldBeFixed;

    if (!changeToFixed && !changeToUnfixed) {
      return;
    }

    if (changeToFixed && this.staySticky) {
      this.observer.unobserve(this.observedElement);
    }

    this.updateCss(shouldBeFixed, urgencyMessaging);
    this.onStateChange(this);
  }

  updateCss(shouldBeFixed, urgencyMessaging) {
    this.fixed = shouldBeFixed;
    if (shouldBeFixed) {
      if (urgencyMessaging) {
        // for the next time we dont want this to be called
        this.urgencyMessaging.shouldNotify = false;
        this.urgencyMessaging.notify();
        this.staySticky = true;
        setTimeout(() => {
          if (this.staySticky) {
            this.$element.classList.add(...this.stickyClasses);
          }
        }, this.urgencyMessaging.delay);
      } else {
        this.$element.classList.add(...this.stickyClasses);
      }
    } else {
      this.staySticky = false;
      this.$element.classList.remove(...this.stickyClasses);
    }
  }
}

module.exports = StickyHandler;
